/**
 * PostgREST data access: BFF at `VITE_API_URL` / `VITE_PUBLIC_APP_ORIGIN` + `/api/app/rest/v1` → Supabase.
 * When BFF is off, uses `VITE_SUPABASE_URL/rest/v1` directly.
 */
import { PostgrestClient } from "@supabase/postgrest-js";
import { authDebugLog, getFreshUserAccessToken, safeRefresh } from "./accessTokenRefresh";
import { appendStorefrontHostHeader, getAppApiBase, isAppApiConfigured, joinAppApiPath } from "./appApi";
import { supabaseAnonKey, supabaseUrl } from "./supabaseEnv";

const supabaseAnon = supabaseAnonKey;

/**
 * PostgrestClient uses `new URL()` internally; relative paths must be absolute.
 * Production: always central origin (never `window.location` on merchant domains).
 */
function absolutePostgrestProxyUrl(path: string): string {
  const p = joinAppApiPath(path);
  if (/^https?:\/\//i.test(p)) return p.replace(/\/+$/, "");
  if (import.meta.env.DEV && getAppApiBase() === "") {
    const origin =
      typeof window !== "undefined" && window.location?.origin ? window.location.origin.replace(/\/$/, "") : "";
    if (!origin) {
      throw new Error("PostgREST BFF: use Vite dev server origin or set VITE_API_URL.");
    }
    const pathOnly = p.startsWith("/") ? p : `/${p}`;
    return `${origin}${pathOnly}`.replace(/\/+$/, "");
  }
  throw new Error(
    "PostgREST BFF URL must be absolute in production. Set VITE_API_URL=https://usefastsell.com or VITE_PUBLIC_APP_ORIGIN.",
  );
}

const proxyFetch: typeof fetch = async (input, init) => {
  const send = async (userToken: string | null) => {
    const headers = new Headers(init?.headers);
    headers.set("apikey", supabaseAnon);
    appendStorefrontHostHeader(headers);
    headers.set("Authorization", `Bearer ${userToken ?? supabaseAnon}`);
    return fetch(input, { ...init, headers });
  };

  let userToken = await getFreshUserAccessToken();
  let res = await send(userToken);
  if (res.status === 401 && userToken) {
    authDebugLog("dataDb proxyFetch: 401 → safeRefresh + retry");
    await safeRefresh();
    userToken = await getFreshUserAccessToken();
    res = await send(userToken);
  }
  if (res.status === 401) {
    res = await send(null);
  }
  return res;
};

let _proxy: PostgrestClient | null = null;
function getProxyClient(): PostgrestClient {
  if (!_proxy) {
    const normalized = absolutePostgrestProxyUrl("/api/app/rest/v1");
    _proxy = new PostgrestClient(normalized, {
      schema: "public",
      fetch: proxyFetch,
    });
  }
  return _proxy;
}

let _direct: PostgrestClient | null = null;
function getDirectClient(): PostgrestClient {
  if (_direct) return _direct;
  const root = supabaseUrl.replace(/\/$/, "");
  if (!root) {
    throw new Error("VITE_SUPABASE_URL is required when BFF is not configured (direct PostgREST).");
  }
  _direct = new PostgrestClient(`${root}/rest/v1`, {
    schema: "public",
    fetch: proxyFetch,
  });
  return _direct;
}

function getRest(): PostgrestClient {
  return isAppApiConfigured() ? getProxyClient() : getDirectClient();
}

/**
 * Replaces `supabase.from` / `supabase.rpc` / `supabase.schema` for row-level and RPC access.
 * Auth, storage, and realtime stay on `supabase` from `@/lib/supabase` / `supabaseClient`.
 */
export const db = {
  from: (relation: string) => getRest().from(relation),
  rpc: (
    fn: string,
    args: Record<string, unknown> = {},
    opts: { head?: boolean; get?: boolean; count?: "exact" | "planned" | "estimated" } = {},
  ) => getRest().rpc(fn, args, opts),
  schema: (name: string) => getRest().schema(name),
};
